import { Pagination, useMediaQuery } from "@mui/material";
import clsx from "clsx";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { AxiosError } from "axios";

import { DeleteV2Icon, EditV2Icon, GraduationCapIcon, InfoIcon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import IconButton from "../../../components/IconButton";
import OpenInNewTabLink from "../../../components/OpenInNewTabLink";
import MemberDetails, {
    MemberDetailsUser,
} from "../../Members/MemberDetails";
import ConfirmModal from "../../../components/ConfirmModal";
import ConnectedLayout from "../../../components/ConnectedLayout";
import Header from "../../../components/Header";
import Input from "../../../components/Input";
import ModalForm, { Field, FieldType } from "../../../components/ModalForm";
import { Select as OldSelect } from "../../../components/OldSelect";
import Select from "../../../components/Select";
import Table from "../../../components/Table";
import { AuthContext } from "../../../contexts/AuthContext";
import { promoteEligibleAdminGraduation } from "../../../services/api/admin/graduation";
import { GRADUATION_VALIDATION_MIN_PROGRESS } from "../../../utils/graduation";
import ImportAssignPromoModal from "./ImportAssignPromoModal";
import RepeatAssignConfirm from "./RepeatAssignConfirm";
import {
    assignUsersToPromotion,
    createPromotion,
    deletePromotion,
    deletePromotionRepeat,
    getLearners,
    getPromotionOptions,
    getPromotionRepeats,
    getPromotions,
    readRepeatConfirmationUserIds,
    unassignLearnerPromotion,
    updatePromotion,
    AssignPromotionResult,
    LearnerRow,
    Promotion,
    PromotionRepeatRow,
} from "../../../services/api/admin/promotions";
import { getFormattedDateInput, getFormattedFrDate } from "../../../utils/date";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";
import { getUserName } from "../../../utils/string";
import styles from "./styles.module.scss";

type TabKey = "learners" | "promotions" | "repeats";

const toInputDate = (value?: string | null) => {
    if (!value) return "";
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return getFormattedDateInput(date);
};

const sortPromotionsByLaunchDesc = (rows: Promotion[]): Promotion[] =>
    [...rows].sort(
        (a, b) =>
            new Date(b.launchDate).getTime() - new Date(a.launchDate).getTime()
    );

const isConcretePromotionId = (value: string) =>
    value !== "" && value !== "none";

const PromotionsAdmin: FC = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const { hasPermission, isStreamOn } = useContext(AuthContext)!;
    const canCreate = hasPermission("admin-promotions", "create");
    const canUpdate = hasPermission("admin-promotions", "update");
    const canDelete = hasPermission("admin-promotions", "delete");
    const canReadRepeats = hasPermission("admin-promotion-repeats", "read");
    const canDeleteRepeats = hasPermission("admin-promotion-repeats", "delete");
    const canImportCsv = hasPermission("admin-members-bulk", "update");
    const canPromoteEligible = hasPermission("admin-graduation", "validate");

    const [activeTab, setActiveTab] = useState<TabKey>("learners");
    const [notesUser, setNotesUser] = useState<MemberDetailsUser | null>(null);

    const [promoOptions, setPromoOptions] = useState<Promotion[]>([]);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [learners, setLearners] = useState<LearnerRow[]>([]);
    const [loading, setLoading] = useState(true);

    const [learnersPage, setLearnersPage] = useState(1);
    const [learnersMaxPage, setLearnersMaxPage] = useState(1);
    const [learnersTotal, setLearnersTotal] = useState(0);
    const [learnersCount, setLearnersCount] = useState<number | null>(null);
    const [learnersSearch, setLearnersSearch] = useState("");
    const [debouncedLearnersSearch, setDebouncedLearnersSearch] = useState("");
    const [promotionFilter, setPromotionFilter] = useState<string>("");
    const [anomalyFilter, setAnomalyFilter] = useState<"" | "only" | "none">(
        ""
    );

    const [promosPage, setPromosPage] = useState(1);
    const [promosMaxPage, setPromosMaxPage] = useState(1);
    const [promosTotal, setPromosTotal] = useState(0);
    const [promosSearch, setPromosSearch] = useState("");
    const [debouncedPromosSearch, setDebouncedPromosSearch] = useState("");
    const [repeats, setRepeats] = useState<PromotionRepeatRow[]>([]);
    const [repeatsPage, setRepeatsPage] = useState(1);
    const [repeatsLimit, setRepeatsLimit] = useState(20);
    const [repeatsMaxPage, setRepeatsMaxPage] = useState(1);
    const [repeatsTotal, setRepeatsTotal] = useState(0);
    const [repeatsSearch, setRepeatsSearch] = useState("");
    const [debouncedRepeatsSearch, setDebouncedRepeatsSearch] = useState("");

    const [formOpen, setFormOpen] = useState(false);
    const [formSaving, setFormSaving] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(
        null
    );
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [assignPromoId, setAssignPromoId] = useState("");
    const [assigning, setAssigning] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [hasEverSelected, setHasEverSelected] = useState(false);
    const [importCsvOpen, setImportCsvOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
    const [promoToDelete, setPromoToDelete] = useState<Promotion | null>(null);
    const [deletingPromo, setDeletingPromo] = useState(false);
    const [repeatToDelete, setRepeatToDelete] = useState<{
        id: string;
        label: string;
    } | null>(null);
    const [deletingRepeat, setDeletingRepeat] = useState(false);
    const [bulkPromoteConfirmOpen, setBulkPromoteConfirmOpen] = useState(false);
    const [bulkPromoteLoading, setBulkPromoteLoading] = useState(false);
    const [repeatPrompt, setRepeatPrompt] = useState<{
        count: number;
        promotionId: string;
        userIds: string[];
        resolve: (result: AssignPromotionResult | undefined) => void;
        reject: (error: unknown) => void;
    } | null>(null);
    const [repeatLoading, setRepeatLoading] = useState(false);

    const [formName, setFormName] = useState("");
    const [formLaunchDate, setFormLaunchDate] = useState(
        getFormattedDateInput(new Date())
    );
    const [formExpertAt, setFormExpertAt] = useState("");
    const [formProAt, setFormProAt] = useState("");
    const [formIndependantAt, setFormIndependantAt] = useState("");
    const [formFormeAt, setFormFormeAt] = useState("");
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});
    const [assignFieldErrors, setAssignFieldErrors] = useState<FieldErrors>(
        {}
    );

    const learnersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const promosDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const latestLearnersRequestId = useRef(0);
    const latestPromosRequestId = useRef(0);
    const latestRepeatsRequestId = useRef(0);
    const repeatsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const qTab = params.get("tab");
        if (qTab === "promotions" && canCreate) {
            setActiveTab("promotions");
        } else if (
            qTab === "learners" ||
            (qTab === "repeats" && canReadRepeats)
        ) {
            setActiveTab(qTab);
        }
        const qSearch = params.get("search");
        if (qSearch) {
            setLearnersSearch(qSearch);
            setDebouncedLearnersSearch(qSearch.trim());
        }
        const qPromotionId = params.get("promotionId");
        if (qPromotionId !== null) {
            setPromotionFilter(qPromotionId);
        }
    }, [canReadRepeats, canCreate]);

    useEffect(() => {
        if (!canCreate && activeTab === "promotions") {
            setActiveTab("learners");
        }
    }, [canCreate, activeTab]);

    useEffect(() => {
        if (learnersDebounceRef.current)
            clearTimeout(learnersDebounceRef.current);
        learnersDebounceRef.current = setTimeout(() => {
            setDebouncedLearnersSearch(learnersSearch.trim());
            setLearnersPage(1);
        }, 350);
        return () => {
            if (learnersDebounceRef.current)
                clearTimeout(learnersDebounceRef.current);
        };
    }, [learnersSearch]);

    useEffect(() => {
        if (promosDebounceRef.current) clearTimeout(promosDebounceRef.current);
        promosDebounceRef.current = setTimeout(() => {
            setDebouncedPromosSearch(promosSearch.trim());
            setPromosPage(1);
        }, 350);
        return () => {
            if (promosDebounceRef.current)
                clearTimeout(promosDebounceRef.current);
        };
    }, [promosSearch]);

    useEffect(() => {
        if (repeatsDebounceRef.current) clearTimeout(repeatsDebounceRef.current);
        repeatsDebounceRef.current = setTimeout(() => {
            setDebouncedRepeatsSearch(repeatsSearch.trim());
            setRepeatsPage(1);
        }, 350);
        return () => {
            if (repeatsDebounceRef.current)
                clearTimeout(repeatsDebounceRef.current);
        };
    }, [repeatsSearch]);

    useEffect(() => {
        if (selectedIds.length > 0) {
            setHasEverSelected(true);
            setIsExiting(false);
        } else if (selectedIds.length === 0 && hasEverSelected && !isExiting) {
            setIsExiting(true);
            setAssignPromoId("");
        }
    }, [selectedIds.length, hasEverSelected, isExiting]);

    useEffect(() => {
        getPromotionOptions()
            .then((rows) =>
                setPromoOptions(sortPromotionsByLaunchDesc(rows ?? []))
            )
            .catch(() => toast.error("Erreur chargement des promotions"));
    }, [refreshKey]);

    useEffect(() => {
        let cancelled = false;
        getLearners({ page: 1, limit: 1 })
            .then((res) => {
                if (!cancelled) setLearnersCount(res?.total ?? 0);
            })
            .catch(() => {
                if (!cancelled) setLearnersCount(null);
            });
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    useEffect(() => {
        if (activeTab !== "learners") return;
        const requestId = ++latestLearnersRequestId.current;
        setLoading(true);
        getLearners({
            page: learnersPage,
            limit: 20,
            search: debouncedLearnersSearch || undefined,
            promotionId: promotionFilter || undefined,
            anomaly: anomalyFilter || undefined,
        })
            .then((res) => {
                if (requestId !== latestLearnersRequestId.current) return;
                setLearners(res?.data ?? []);
                setLearnersMaxPage(res?.maxPage ?? 1);
                setLearnersTotal(res?.total ?? 0);
            })
            .catch(() => {
                if (requestId !== latestLearnersRequestId.current) return;
                toast.error("Erreur chargement des apprenants");
            })
            .finally(() => {
                if (requestId === latestLearnersRequestId.current)
                    setLoading(false);
            });
    }, [
        activeTab,
        learnersPage,
        debouncedLearnersSearch,
        promotionFilter,
        anomalyFilter,
        refreshKey,
    ]);

    useEffect(() => {
        if (activeTab !== "promotions") return;
        const requestId = ++latestPromosRequestId.current;
        setLoading(true);
        getPromotions({
            page: promosPage,
            limit: 20,
            search: debouncedPromosSearch || undefined,
        })
            .then((res) => {
                if (requestId !== latestPromosRequestId.current) return;
                setPromotions(sortPromotionsByLaunchDesc(res?.data ?? []));
                setPromosMaxPage(res?.maxPage ?? 1);
                setPromosTotal(res?.total ?? 0);
            })
            .catch(() => {
                if (requestId !== latestPromosRequestId.current) return;
                toast.error("Erreur chargement des promotions");
            })
            .finally(() => {
                if (requestId === latestPromosRequestId.current)
                    setLoading(false);
            });
    }, [activeTab, promosPage, debouncedPromosSearch, refreshKey]);

    useEffect(() => {
        if (!canReadRepeats) return;
        const requestId = ++latestRepeatsRequestId.current;
        if (activeTab === "repeats") setLoading(true);
        getPromotionRepeats({
            page: repeatsPage,
            limit: repeatsLimit,
            search: debouncedRepeatsSearch || undefined,
        })
            .then((res) => {
                if (requestId !== latestRepeatsRequestId.current) return;
                setRepeats(res?.data ?? []);
                setRepeatsMaxPage(res?.maxPage ?? 1);
                setRepeatsTotal(res?.total ?? 0);
            })
            .catch(() => {
                if (requestId !== latestRepeatsRequestId.current) return;
                toast.error("Erreur chargement des redoublements");
            })
            .finally(() => {
                if (
                    requestId === latestRepeatsRequestId.current &&
                    activeTab === "repeats"
                ) {
                    setLoading(false);
                }
            });
    }, [
        activeTab,
        canReadRepeats,
        repeatsPage,
        repeatsLimit,
        debouncedRepeatsSearch,
        refreshKey,
    ]);

    const promotionFilterOptions = useMemo(
        () => [
            { value: "", label: "Toutes les promos" },
            { value: "none", label: "Sans promo" },
            ...promoOptions.map((p) => ({ value: p.id, label: p.name })),
        ],
        [promoOptions]
    );

    const assignOptions = useMemo(
        () =>
            promoOptions.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.usersCount})`,
            })),
        [promoOptions]
    );

    const resetForm = () => {
        setEditingPromotion(null);
        setFormName("");
        setFormLaunchDate(getFormattedDateInput(new Date()));
        setFormExpertAt("");
        setFormProAt("");
        setFormIndependantAt("");
        setFormFormeAt("");
        setFieldErrorsState({});
    };

    const openCreateForm = () => {
        resetForm();
        setFormOpen(true);
    };

    const openEditForm = (promo: Promotion) => {
        setEditingPromotion(promo);
        setFormName(promo.name);
        setFormLaunchDate(toInputDate(promo.launchDate));
        setFormExpertAt(toInputDate(promo.plannedExpertAt));
        setFormProAt(toInputDate(promo.plannedProAt));
        setFormIndependantAt(toInputDate(promo.plannedIndependantAt));
        setFormFormeAt(toInputDate(promo.plannedFormeAt));
        setFormOpen(true);
    };

    const closeForm = () => {
        setFormOpen(false);
        resetForm();
    };

    const handleSavePromotion = async () => {
        const nextErrors = setFieldErrors(
            ["name", !formName.trim() && REQUIRED_FIELD],
            ["launchDate", !formLaunchDate && REQUIRED_FIELD]
        );
        setFieldErrorsState(nextErrors);
        const promoToastMessage = nextErrors.name
            ? "Le nom de la promotion est requis"
            : nextErrors.launchDate
              ? "La date de lancement est requise"
              : undefined;
        if (
            notifyFieldErrors(nextErrors, {
                message: promoToastMessage,
                type: "warning",
            })
        ) {
            return;
        }

        const payload = {
            name: formName.trim(),
            launchDate: formLaunchDate,
            plannedExpertAt: formExpertAt || null,
            plannedProAt: formProAt || null,
            plannedIndependantAt: formIndependantAt || null,
            plannedFormeAt: formFormeAt || null,
        };

        setFormSaving(true);
        try {
            if (editingPromotion) {
                await updatePromotion(editingPromotion.id, payload);
                toast.success("Promotion mise à jour");
            } else {
                await createPromotion(payload);
                toast.success("Promotion créée");
            }
            closeForm();
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error(
                editingPromotion
                    ? "Échec de la mise à jour de la promotion"
                    : "Échec de la création de la promotion"
            );
        } finally {
            setFormSaving(false);
        }
    };

    const handleConfirmDeleteRepeat = async () => {
        if (!repeatToDelete) return;
        setDeletingRepeat(true);
        try {
            await deletePromotionRepeat(repeatToDelete.id);
            toast.success("Redoublement supprimé");
            setRepeatToDelete(null);
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Échec de la suppression du redoublement");
        } finally {
            setDeletingRepeat(false);
        }
    };

    const handleConfirmDeletePromo = async () => {
        if (!promoToDelete) return;
        setDeletingPromo(true);
        try {
            await deletePromotion(promoToDelete.id);
            toast.success(
                `Promotion « ${promoToDelete.name} » supprimée. Les membres restent en compte (sans promo).`
            );
            setPromoToDelete(null);
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Échec de la suppression de la promotion");
        } finally {
            setDeletingPromo(false);
        }
    };

    const assignWithRepeatCheck = (
        promotionId: string,
        userIds: string[]
    ) =>
        new Promise<AssignPromotionResult | undefined>((resolve, reject) => {
            const attempt = async (countAsRepeat?: boolean) => {
                try {
                    const result = await assignUsersToPromotion(
                        promotionId,
                        userIds,
                        countAsRepeat
                    );
                    resolve(result);
                } catch (error) {
                    const changingIds = readRepeatConfirmationUserIds(error);
                    if (!changingIds || countAsRepeat !== undefined) {
                        reject(error);
                        return;
                    }
                    setRepeatPrompt({
                        count: changingIds.length,
                        promotionId,
                        userIds,
                        resolve,
                        reject,
                    });
                }
            };
            void attempt();
        });

    const finishRepeatChoice = async (countAsRepeat: boolean) => {
        if (!repeatPrompt) return;
        setRepeatLoading(true);
        try {
            const result = await assignUsersToPromotion(
                repeatPrompt.promotionId,
                repeatPrompt.userIds,
                countAsRepeat
            );
            repeatPrompt.resolve(result);
            setRepeatPrompt(null);
        } catch (error) {
            repeatPrompt.reject(error);
            setRepeatPrompt(null);
        } finally {
            setRepeatLoading(false);
        }
    };

    const handleAssign = async () => {
        const nextErrors = setFieldErrors(
            ["assignPromoId", !assignPromoId && REQUIRED_FIELD],
            [
                "selectedIds",
                selectedIds.length === 0 &&
                    "Sélectionnez au moins un apprenant",
            ]
        );
        setAssignFieldErrors(nextErrors);
        const assignToastMessage = nextErrors.assignPromoId
            ? "Choisissez une promotion"
            : nextErrors.selectedIds
              ? "Sélectionnez au moins un apprenant"
              : undefined;
        if (
            notifyFieldErrors(nextErrors, {
                message: assignToastMessage,
                type: "warning",
            })
        ) {
            return;
        }
        setAssigning(true);
        try {
            const result = await assignWithRepeatCheck(
                assignPromoId,
                selectedIds
            );
            if (!result) return;
            const promoted = result.promotedToJunior ?? 0;
            const repeats = result.repeatsRecorded ?? 0;
            toast.success(
                [
                    `${result.updated ?? selectedIds.length} apprenant(s) mis à jour`,
                    promoted > 0
                        ? `${promoted} passé${promoted > 1 ? "s" : ""} Junior`
                        : null,
                    repeats > 0
                        ? `${repeats} redoublement${repeats > 1 ? "s" : ""}`
                        : null,
                ]
                    .filter(Boolean)
                    .join(" · ")
            );
            setAssignPromoId("");
            setIsExiting(true);
            setTimeout(() => {
                setSelectedIds([]);
                setIsExiting(false);
                setHasEverSelected(false);
            }, 300);
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Échec de l'attribution");
        } finally {
            setAssigning(false);
        }
    };

    const selectedPromotionName =
        promoOptions.find((promo) => promo.id === promotionFilter)?.name ??
        "cette promotion";
    const canBulkPromoteEligible =
        canPromoteEligible &&
        activeTab === "learners" &&
        isConcretePromotionId(promotionFilter);

    const handleConfirmBulkPromoteEligible = async () => {
        if (!isConcretePromotionId(promotionFilter)) return;
        setBulkPromoteLoading(true);
        try {
            const result = await promoteEligibleAdminGraduation(promotionFilter);
            const promotedCount = result?.promotedCount ?? 0;
            const failedCount = result?.failedCount ?? 0;
            if (failedCount > 0) {
                toast.warning(
                    `${promotedCount} promotion(s) effectuée(s), ${failedCount} échec(s).`
                );
            } else if (promotedCount === 0) {
                toast.info(
                    `Aucun membre à ${GRADUATION_VALIDATION_MIN_PROGRESS} % ou plus à promouvoir dans cette promo.`
                );
            } else {
                toast.success(
                    `${promotedCount} membre(s) à ${GRADUATION_VALIDATION_MIN_PROGRESS} % ou plus promu(s) (${selectedPromotionName}).`
                );
            }
            setBulkPromoteConfirmOpen(false);
            setRefreshKey((k) => k + 1);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                toast.error("Vous n'avez pas les droits pour promouvoir");
                return;
            }
            toast.error("Échec de la promotion groupée des éligibles.");
        } finally {
            setBulkPromoteLoading(false);
        }
    };

    const handleViewTrades = (userId: string) => {
        sessionStorage.setItem(
            "returnToSearchUrl",
            window.location.pathname + window.location.search
        );
        navigate(`/trades?userId=${userId}`);
    };

    const handlePromotionChange = async (
        user: LearnerRow,
        nextPromotionId: string
    ) => {
        const current = user.promotionId ?? "";
        if (nextPromotionId === current) return;

        setUpdatingUserId(user.id);
        try {
            if (!nextPromotionId) {
                await unassignLearnerPromotion(user.id);
                toast.success("Promo retirée");
            } else {
                const result = await assignWithRepeatCheck(nextPromotionId, [
                    user.id,
                ]);
                if (!result) return;
                toast.success(
                    (result.repeatsRecorded ?? 0) > 0
                        ? "Promo mise à jour · redoublement enregistré"
                        : "Promo mise à jour"
                );
            }
            setRefreshKey((k) => k + 1);
        } catch {
            toast.error("Échec de la mise à jour de la promo");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const formFields: Field<FieldType>[] = [
        {
            id: "name",
            label: "Nom de la promotion *",
            type: "text",
            value: formName,
            onChange: (value) => {
                setFormName(value);
                setFieldErrorsState((prev) => clearFieldError(prev, "name"));
            },
            error: fieldErrors.name,
        },
        {
            id: "launchDate",
            label: "Date de lancement (rentrée) *",
            type: "oldDate",
            value: formLaunchDate,
            onChange: (value) => {
                setFormLaunchDate(value);
                setFieldErrorsState((prev) =>
                    clearFieldError(prev, "launchDate")
                );
            },
            error: fieldErrors.launchDate,
        },
        {
            id: "plannedExpertAt",
            label: "Passage Expert prévu",
            type: "oldDate",
            value: formExpertAt,
            onChange: setFormExpertAt,
        },
        {
            id: "plannedProAt",
            label: "Passage Pro prévu",
            type: "oldDate",
            value: formProAt,
            onChange: setFormProAt,
        },
        {
            id: "plannedIndependantAt",
            label: "Passage Indépendant prévu",
            type: "oldDate",
            value: formIndependantAt,
            onChange: setFormIndependantAt,
        },
        {
            id: "plannedFormeAt",
            label: "Passage Formé prévu",
            type: "oldDate",
            value: formFormeAt,
            onChange: setFormFormeAt,
        },
    ];

    const learnerColumns = [
        { label: "Apprenant", value: "name", width: 200 },
        { label: "Email", value: "email", width: 220 },
        { label: "Rôle", value: "role", width: 120 },
        { label: "Anomalie Prog.", value: "anomaly", width: 140 },
        { label: "Promotion", value: "promo", width: 200 },
        { label: "Nbs de redoublements", value: "repeatCount", width: 160 },
        { label: "Lancement", value: "launch", width: 120 },
    ];

    const learnerRows = learners.map((user) => ({
        id: user.id,
        name: (
            <span className={styles.nameCell}>
                <span
                    className={styles.notesIcon}
                    onClick={(event) => event.stopPropagation()}
                >
                    <IconButton
                        size="medium"
                        icon={InfoIcon}
                        onClick={() =>
                            setNotesUser({
                                id: user.id,
                                firstname: user.firstname ?? "",
                                lastname: user.lastname ?? "",
                                pseudo: user.pseudo ?? "",
                                email: user.email,
                            })
                        }
                    />
                </span>
                <span
                    className={styles.nameLink}
                    onClick={() => handleViewTrades(user.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleViewTrades(user.id);
                        }
                    }}
                >
                    {getUserName(
                        user.firstname ?? "",
                        user.lastname ?? "",
                        user.pseudo ?? user.email,
                        isStreamOn
                    ) || "—"}
                </span>
                <OpenInNewTabLink
                    href={`/trades?userId=${encodeURIComponent(user.id)}`}
                />
            </span>
        ),
        email: user.email,
        role: user.roleName ?? "—",
        anomaly:
            user.expectedRole && user.expectedRole !== user.roleName ? (
                <span
                    className={styles.anomalyBadge}
                    title={`Rôle calendrier : ${user.expectedRole}`}
                >
                    {user.expectedRole}
                </span>
            ) : (
                "—"
            ),
        repeatCount: user.repeatCount ?? 0,
        promo: canUpdate ? (
            <div
                className={styles.promoSelectWrap}
                onClick={(e) => e.stopPropagation()}
            >
                <OldSelect
                    className={styles.promoSelect}
                    value={user.promotionId ?? ""}
                    disabled={updatingUserId === user.id}
                    onChanged={(value) => {
                        void handlePromotionChange(user, String(value ?? ""));
                    }}
                >
                    <option value="">Sans promo</option>
                    {promoOptions.map((promo) => (
                        <option key={promo.id} value={promo.id}>
                            {promo.name}
                        </option>
                    ))}
                </OldSelect>
            </div>
        ) : (
            user.promotionName ?? (
                <span className={styles.muted}>Sans promo</span>
            )
        ),
        launch: user.promotionLaunchDate
            ? getFormattedFrDate(new Date(user.promotionLaunchDate))
            : "—",
    }));

    const promoColumns = [
        { label: "Nom", value: "name", width: 180 },
        { label: "Lancement", value: "launch", width: 120 },
        { label: "Apprenants", value: "count", width: 100 },
        { label: "", value: "actions", width: 96 },
    ];

    const promoRows = promotions.map((promo) => ({
        id: promo.id,
        name: promo.name,
        launch: getFormattedFrDate(new Date(promo.launchDate)),
        count: promo.usersCount,
        actions:
            canUpdate || canDelete ? (
                <div className={styles.rowActions}>
                    {canUpdate && (
                        <IconButton
                            size="medium"
                            icon={EditV2Icon}
                            onClick={() => openEditForm(promo)}
                        />
                    )}
                    {canDelete && (
                        <IconButton
                            size="medium"
                            icon={DeleteV2Icon}
                            onClick={() => setPromoToDelete(promo)}
                        />
                    )}
                </div>
            ) : null,
    }));

    return (
        <ConnectedLayout>
            <ConfirmModal
                open={repeatToDelete !== null}
                handleClose={() => {
                    if (!deletingRepeat) setRepeatToDelete(null);
                }}
                handleConfirm={() => {
                    void handleConfirmDeleteRepeat();
                }}
                isLoading={deletingRepeat}
                title="Supprimer ce redoublement ?"
                confirmLabel="Supprimer"
                description={
                    repeatToDelete
                        ? `« ${repeatToDelete.label} » sera retiré de l'historique. Le nombre de redoublements diminuera de 1.`
                        : undefined
                }
            />
            <ConfirmModal
                open={promoToDelete !== null}
                handleClose={() => {
                    if (!deletingPromo) setPromoToDelete(null);
                }}
                handleConfirm={handleConfirmDeletePromo}
                isLoading={deletingPromo}
                title="Supprimer la promotion ?"
                description={
                    promoToDelete
                        ? `« ${promoToDelete.name} » sera supprimée. Les ${promoToDelete.usersCount} membre(s) rattaché(s) ne seront pas supprimés : ils passeront simplement sans promotion.`
                        : undefined
                }
                confirmLabel="Supprimer"
            />
            <ConfirmModal
                open={bulkPromoteConfirmOpen}
                handleClose={() => {
                    if (!bulkPromoteLoading) setBulkPromoteConfirmOpen(false);
                }}
                handleConfirm={() => {
                    void handleConfirmBulkPromoteEligible();
                }}
                isLoading={bulkPromoteLoading}
                title={`Promouvoir à partir de ${GRADUATION_VALIDATION_MIN_PROGRESS} % ?`}
                confirmLabel="Promouvoir"
                confirmVariant="primary"
                description={`Vous allez promouvoir tous les membres de « ${selectedPromotionName} » dont le score global est d'au moins ${GRADUATION_VALIDATION_MIN_PROGRESS} %, comme le bouton de promotion individuelle. Les autres promotions ne sont pas concernées.`}
            />
            <RepeatAssignConfirm
                open={repeatPrompt !== null}
                count={repeatPrompt?.count ?? 0}
                loading={repeatLoading}
                onClose={() => {
                    if (repeatLoading) return;
                    repeatPrompt?.resolve(undefined);
                    setRepeatPrompt(null);
                }}
                onNo={() => {
                    void finishRepeatChoice(false);
                }}
                onYes={() => {
                    void finishRepeatChoice(true);
                }}
            />
            <Header icon={GraduationCapIcon} title="Promotions" />
            <div className={styles.container}>
                <div className={styles.titleBlock}>
                    <p className={styles.subtitle}>
                        Gérez les cohortes et attribuez les apprenants. Un Trader
                        Inscrit passe Junior à l&apos;affectation.
                    </p>
                </div>

                <div className={styles.tabs}>
                    <Button
                        size="medium"
                        variant={
                            activeTab === "learners" ? "secondary" : "transparent"
                        }
                        label={`Apprenants (${
                            activeTab === "learners"
                                ? learnersTotal
                                : learnersCount ?? "…"
                        })`}
                        onClick={() => setActiveTab("learners")}
                    />
                    {canCreate && (
                        <Button
                            size="medium"
                            variant={
                                activeTab === "promotions"
                                    ? "secondary"
                                    : "transparent"
                            }
                            label={`Promotions (${
                                activeTab === "promotions"
                                    ? promosTotal
                                    : promoOptions.length
                            })`}
                            onClick={() => setActiveTab("promotions")}
                        />
                    )}
                    {canReadRepeats && (
                        <Button
                            size="medium"
                            variant={
                                activeTab === "repeats"
                                    ? "secondary"
                                    : "transparent"
                            }
                            label={`Redoublements (${
                                activeTab === "repeats" ? repeatsTotal : "…"
                            })`}
                            onClick={() => setActiveTab("repeats")}
                        />
                    )}
                </div>

                {activeTab === "repeats" && canReadRepeats ? (
                    <>
                        <div className={styles.filtersCard}>
                            <div className={styles.filtersRow}>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>
                                        Recherche
                                    </span>
                                    <Input
                                        className={styles.searchInput}
                                        placeholder="Nom, email, promo…"
                                        value={repeatsSearch}
                                        onChanged={setRepeatsSearch}
                                    />
                                </div>
                            </div>
                        </div>
                        <Table
                            className={styles.tableMin}
                            rows={[
                                { label: "Apprenant", value: "name", width: 200 },
                                { label: "De", value: "from", width: 160 },
                                { label: "Vers", value: "to", width: 160 },
                                { label: "Admin", value: "admin", width: 180 },
                                { label: "Date", value: "date", width: 120 },
                                ...(canDeleteRepeats
                                    ? [{ label: "", value: "actions", width: 64 }]
                                    : []),
                            ]}
                            data={repeats.map((repeat) => ({
                                id: repeat.id,
                                name: (
                                    <span className={styles.nameCell}>
                                        <span
                                            className={styles.notesIcon}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <IconButton
                                                size="medium"
                                                icon={InfoIcon}
                                                onClick={() =>
                                                    setNotesUser({
                                                        id: repeat.userId,
                                                        firstname:
                                                            repeat.firstname ??
                                                            "",
                                                        lastname:
                                                            repeat.lastname ??
                                                            "",
                                                        pseudo:
                                                            repeat.pseudo ?? "",
                                                        email: repeat.email,
                                                    })
                                                }
                                            />
                                        </span>
                                        <span>
                                            {getUserName(
                                                repeat.firstname ?? "",
                                                repeat.lastname ?? "",
                                                repeat.pseudo ?? repeat.email,
                                                isStreamOn
                                            ) || "—"}
                                        </span>
                                    </span>
                                ),
                                from: repeat.fromPromotionName,
                                to: repeat.toPromotionName,
                                admin:
                                    getUserName(
                                        repeat.createdBy?.firstname ?? "",
                                        repeat.createdBy?.lastname ?? "",
                                        repeat.createdBy?.pseudo ??
                                            repeat.createdBy?.email ??
                                            "",
                                        isStreamOn
                                    ) || "—",
                                date: getFormattedFrDate(
                                    new Date(repeat.createdAt)
                                ),
                                actions: canDeleteRepeats ? (
                                    <IconButton
                                        size="medium"
                                        icon={DeleteV2Icon}
                                        onClick={() =>
                                            setRepeatToDelete({
                                                id: repeat.id,
                                                label: `${repeat.fromPromotionName} → ${repeat.toPromotionName}`,
                                            })
                                        }
                                    />
                                ) : null,
                            }))}
                            loading={loading}
                            noDataLabel="Aucun redoublement"
                        />
                        <div className={styles.paginationRow}>
                            <Pagination
                                className={styles.pagination}
                                count={Math.max(1, repeatsMaxPage)}
                                page={repeatsPage}
                                onChange={(_e, value) => setRepeatsPage(value)}
                                size={isMobile ? "small" : "medium"}
                            />
                            <OldSelect
                                value={String(repeatsLimit)}
                                onChanged={(value) => {
                                    setRepeatsLimit(Number(value));
                                    setRepeatsPage(1);
                                }}
                                className={styles.paginationSelect}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </OldSelect>
                        </div>
                    </>
                ) : activeTab === "learners" ? (
                    <>
                        <div className={styles.filtersCard}>
                            <div className={styles.filtersRow}>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>
                                        Recherche
                                    </span>
                                    <Input
                                        className={styles.searchInput}
                                        placeholder="Nom, email…"
                                        value={learnersSearch}
                                        onChanged={setLearnersSearch}
                                    />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>
                                        Anomalie
                                    </span>
                                    <Select
                                        classNameTrigger={styles.filterSelect}
                                        options={[
                                            { value: "", label: "Toutes" },
                                            {
                                                value: "only",
                                                label: "Avec anomalie",
                                            },
                                            {
                                                value: "none",
                                                label: "Sans anomalie",
                                            },
                                        ]}
                                        value={anomalyFilter}
                                        type="single"
                                        onChange={(v) => {
                                            const next = (v as string) || "";
                                            setAnomalyFilter(
                                                next === "only" || next === "none"
                                                    ? next
                                                    : ""
                                            );
                                            setLearnersPage(1);
                                        }}
                                    />
                                </div>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>
                                        Promotion
                                    </span>
                                    <Select
                                        classNameTrigger={styles.filterSelect}
                                        options={promotionFilterOptions}
                                        value={promotionFilter}
                                        type="single"
                                        onChange={(v) => {
                                            setPromotionFilter(
                                                (v as string) || ""
                                            );
                                            setLearnersPage(1);
                                        }}
                                    />
                                </div>
                                {canBulkPromoteEligible && (
                                    <div className={styles.filterResetField}>
                                        <Button
                                            label={`Promouvoir toutes les personnes à ${GRADUATION_VALIDATION_MIN_PROGRESS} %`}
                                            labelOptions={{ noWrap: true }}
                                            size="medium"
                                            variant="primary"
                                            disabled={bulkPromoteLoading || loading}
                                            onClick={() =>
                                                setBulkPromoteConfirmOpen(true)
                                            }
                                        />
                                    </div>
                                )}
                                {canImportCsv && (
                                    <div className={styles.filterResetField}>
                                        <Button
                                            label="Import CSV"
                                            size="medium"
                                            variant="outlined"
                                            onClick={() =>
                                                setImportCsvOpen(true)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <Table
                            className={styles.tableMin}
                            rows={learnerColumns}
                            data={learnerRows}
                            loading={loading}
                            selectable={canUpdate}
                            selectedIds={selectedIds}
                            onSelectionChange={setSelectedIds}
                            noDataLabel="Aucun apprenant"
                        />

                        {learnersMaxPage > 1 && (
                            <Pagination
                                className={styles.pagination}
                                count={learnersMaxPage}
                                page={learnersPage}
                                onChange={(_e, value) =>
                                    setLearnersPage(value)
                                }
                                size={isMobile ? "small" : "medium"}
                            />
                        )}

                        {canUpdate &&
                            (selectedIds.length > 0 || isExiting) && (
                                <div
                                    className={clsx(
                                        styles.action_bar,
                                        isExiting && styles.action_bar_exit
                                    )}
                                >
                                    <div
                                        className={styles.selection_meta}
                                        aria-label={`${selectedIds.length} sélectionné${
                                            selectedIds.length > 1 ? "s" : ""
                                        }`}
                                    >
                                        <span
                                            className={
                                                styles.selection_count_badge
                                            }
                                        >
                                            {selectedIds.length}
                                        </span>
                                        <span
                                            className={
                                                styles.selection_count_label
                                            }
                                        >
                                            sélectionné
                                            {selectedIds.length > 1 ? "s" : ""}
                                        </span>
                                    </div>
                                    <div className={styles.action_bar_actions}>
                                        <Select
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Attribuer à…",
                                                },
                                                ...assignOptions,
                                            ]}
                                            value={assignPromoId}
                                            type="single"
                                            onChange={(v) => {
                                                setAssignPromoId(
                                                    (v as string) || ""
                                                );
                                                setAssignFieldErrors((prev) =>
                                                    clearFieldError(
                                                        prev,
                                                        "assignPromoId"
                                                    )
                                                );
                                            }}
                                            error={
                                                assignFieldErrors.assignPromoId
                                            }
                                            classNameTrigger={
                                                styles.action_bar_select
                                            }
                                            placement="top-end"
                                        />
                                        <Button
                                            label="Attribuer"
                                            size="medium"
                                            isLoading={assigning}
                                            disabled={
                                                selectedIds.length === 0 ||
                                                !assignPromoId
                                            }
                                            onClick={handleAssign}
                                        />
                                    </div>
                                </div>
                            )}
                    </>
                ) : (
                    <>
                        <div className={styles.filtersCard}>
                            <div className={styles.filtersRow}>
                                <div className={styles.filterField}>
                                    <span className={styles.filterLabel}>
                                        Recherche
                                    </span>
                                    <Input
                                        className={styles.searchInput}
                                        placeholder="Nom de promo…"
                                        value={promosSearch}
                                        onChanged={setPromosSearch}
                                    />
                                </div>
                                {canCreate && (
                                    <div className={styles.filterResetField}>
                                        <Button
                                            label="Nouvelle promo"
                                            size="medium"
                                            onClick={openCreateForm}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <Table
                            className={styles.tableMin}
                            rows={promoColumns}
                            data={promoRows}
                            loading={loading}
                            noDataLabel="Aucune promotion"
                        />

                        {promosMaxPage > 1 && (
                            <Pagination
                                className={styles.pagination}
                                count={promosMaxPage}
                                page={promosPage}
                                onChange={(_e, value) => setPromosPage(value)}
                                size={isMobile ? "small" : "medium"}
                            />
                        )}
                    </>
                )}
            </div>

            <ModalForm
                open={formOpen}
                handleClose={closeForm}
                title={
                    editingPromotion
                        ? `Modifier · ${editingPromotion.name}`
                        : "Nouvelle promotion"
                }
                submitLabel={editingPromotion ? "Enregistrer" : "Créer"}
                onSubmit={handleSavePromotion}
                isLoading={formSaving}
                size="medium"
                fields={formFields}
            />

            <ImportAssignPromoModal
                open={importCsvOpen}
                handleClose={() => setImportCsvOpen(false)}
                onCompleted={() => setRefreshKey((k) => k + 1)}
            />
            {notesUser && (
                <MemberDetails
                    open
                    handleClose={() => setNotesUser(null)}
                    user={notesUser}
                />
            )}
        </ConnectedLayout>
    );
};

export default PromotionsAdmin;
